(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 750px)');
  const targets = [
    ['.sl-video-hero__label', 65],
    ['.cost-comparison__header, .cost-comparison__controls', 0],
    ['.featured-product__media, .featured-product__heading, .featured-product__content .product-price, .featured-product__details', 75],
    ['.craft-story__detail', 110],
    ['.final-story__heading, .final-story__cta', 100],
    ['.site-footer__newsletter', 0],
    ['.product-media-column, .product-block--title_price, .product-block--purchase, .product-block--accordion, .product-description-placement', 55]
  ];
  const reveal = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-sl-visible');
      entry.target.removeAttribute('data-sl-motion-pending');
      reveal.unobserve(entry.target);
    });
  }, { threshold: .2, rootMargin: '0px 0px -5% 0px' });
  const hasRoom = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight * .88;
  };
  const setupReveals = (root = document) => {
    targets.forEach(([selector, stagger]) => {
      const elements = [];
      if (root.matches?.(selector)) elements.push(root);
      elements.push(...root.querySelectorAll(selector));
      elements.forEach((element, index) => {
        if (element.dataset.slMotionInit) return;
        element.dataset.slMotionInit = 'true';
        element.dataset.animate = 'fade-up';
        element.style.setProperty('--sl-motion-delay', `${Math.min(index * stagger, 350)}ms`);
        if (reduced.matches || hasRoom(element)) element.classList.add('is-sl-visible');
        else {
          element.setAttribute('data-sl-motion-pending', '');
          reveal.observe(element);
        }
      });
    });
  };
  const setupHero = (root = document) => {
    const heroes = [];
    if (root.matches?.('[data-cinematic-hero]')) heroes.push(root);
    heroes.push(...root.querySelectorAll('[data-cinematic-hero]'));
    heroes.forEach((hero) => {
      if (hero.dataset.slHeroInit || hero.classList.contains('no-text-reveal') || reduced.matches) return;
      const heading = hero.querySelector('h1');
      if (!heading) return;
      hero.dataset.slHeroInit = 'true';
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      let index = 0;
      nodes.forEach((node) => {
        const fragment = document.createDocumentFragment();
        String(node.textContent).split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { fragment.append(document.createTextNode(part)); return; }
          const word = document.createElement('span');
          word.className = 'sl-hero-word';
          word.style.setProperty('--sl-word-delay', `${Math.min(index++ * 55, 330)}ms`);
          word.textContent = part;
          fragment.append(word);
        });
        node.replaceWith(fragment);
      });
      hero.classList.add('sl-motion-hero-ready');
    });
  };

  const running = new WeakMap();
  const animateAmount = (element, target, duration = 1200) => {
    if (!element || !Number.isFinite(target)) return;
    const decimals = target < 10 ? 2 : 0;
    const format = (value) => '$' + new Intl.NumberFormat('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
    const old = running.get(element);
    if (old) cancelAnimationFrame(old.frame);
    const from = reduced.matches ? target : Number.isFinite(old?.shown) ? old.shown : (element.dataset.slCounted ? Number(element.dataset.slCounted) : 0);
    if (reduced.matches) { element.textContent = format(target); element.dataset.slCounted = String(target); return; }
    const state = { frame: 0, shown: from };
    running.set(element, state);
    const started = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - started) / duration, 1);
      state.shown = from + (target - from) * (1 - Math.pow(1 - progress, 3));
      element.textContent = format(state.shown);
      if (progress < 1) state.frame = requestAnimationFrame(tick);
      else { element.textContent = format(target); element.dataset.slCounted = String(target); running.delete(element); }
    };
    state.frame = requestAnimationFrame(tick);
  };
  const amounts = new WeakSet();
  const moneyObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.querySelectorAll('[data-sl-count]').forEach((element) => animateAmount(element, Number(element.dataset.slCount)));
      moneyObserver.unobserve(entry.target);
    });
  }, { threshold: .2 });
  const setupAmounts = (root = document) => {
    const sections = [];
    if (root.matches?.('.cost-comparison')) sections.push(root);
    sections.push(...root.querySelectorAll('.cost-comparison'));
    sections.forEach((section) => {
      if (amounts.has(section)) return;
      amounts.add(section);
      const nightly = section.querySelector('[data-current-nightly]');
      const reset = section.querySelector('.cost-comparison__number--accent');
      [nightly, reset].forEach((node) => {
        if (node) { node.dataset.slCount = String(Number(node.textContent.replace(/[^\d.]/g, '')) || 0); }
      });
      const description = section.querySelector('.cost-comparison__result--reset .cost-comparison__description');
      if (description && description.childNodes.length === 1 && description.firstChild.nodeType === Node.TEXT_NODE) {
        const match = description.textContent.match(/\$\d+(?:\.\d+)?/);
        if (match) {
          const source = description.textContent;
          const price = document.createElement('span');
          price.dataset.slCount = String(Number(match[0].slice(1)));
          price.textContent = match[0];
          description.replaceChildren(document.createTextNode(source.slice(0, match.index)), price, document.createTextNode(source.slice(match.index + match[0].length)));
        }
      }
      if (reduced.matches || hasRoom(section)) {
        section.querySelectorAll('[data-sl-count]').forEach((node) => animateAmount(node, Number(node.dataset.slCount)));
      } else moneyObserver.observe(section);
    });
  };
  document.addEventListener('sl:cost-change', (event) => {
    const node = event.target.querySelector('[data-current-nightly]');
    const amount = Number(event.detail?.nightly);
    if (!node || !Number.isFinite(amount)) return;
    node.dataset.slCount = amount.toFixed(2);
    if (amounts.has(event.target) && node.dataset.slCounted) animateAmount(node, amount, 500);
    else node.textContent = '$' + amount.toFixed(2);
  });

  const setupComparison = (root = document) => {
    const comparisons = [];
    if (root.matches?.('image-comparison')) comparisons.push(root);
    comparisons.push(...root.querySelectorAll('image-comparison'));
    comparisons.forEach((comparison) => {
      if (comparison.dataset.slDragInit) return;
      comparison.dataset.slDragInit = 'true';
      const range = comparison.querySelector('[data-comparison-range]');
      if (!range) return;
      range.addEventListener('pointerdown', () => { if (!reduced.matches) comparison.classList.add('is-sl-dragging'); });
      const release = () => {
        if (!comparison.classList.contains('is-sl-dragging')) return;
        comparison.classList.remove('is-sl-dragging', 'is-sl-released');
        void comparison.offsetWidth;
        comparison.classList.add('is-sl-released');
        window.setTimeout(() => comparison.classList.remove('is-sl-released'), 400);
      };
      range.addEventListener('pointerup', release);
      range.addEventListener('pointercancel', release);
      window.addEventListener('pointerup', release);
    });
  };
  const setupGallery = (root = document) => {
    const galleries = [];
    if (root.matches?.('edition-product-gallery')) galleries.push(root);
    galleries.push(...root.querySelectorAll('edition-product-gallery'));
    galleries.forEach((gallery) => {
      if (gallery.dataset.slCrossfadeInit) return;
      gallery.dataset.slCrossfadeInit = 'true';
      gallery.closest('[data-main-product]')?.addEventListener('product:variant-change', (event) => {
        if (reduced.matches || String(event.detail?.changedOptionName).toLowerCase() !== 'edition') return;
        gallery.classList.remove('is-sl-edition-changing');
        requestAnimationFrame(() => {
          gallery.classList.add('is-sl-edition-changing');
          window.setTimeout(() => gallery.classList.remove('is-sl-edition-changing'), 400);
        });
      });
    });
  };
  let parallaxQueued = false;
  const updateParallax = () => {
    parallaxQueued = false;
    document.querySelectorAll('.final-story__image').forEach((image) => {
      if (reduced.matches || !desktop.matches) { image.style.removeProperty('--sl-parallax-y'); return; }
      const rect = image.closest('.final-story').getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const progress = Math.max(-1, Math.min(1, (innerHeight / 2 - (rect.top + rect.height / 2)) / ((innerHeight + rect.height) / 2)));
      image.style.setProperty('--sl-parallax-y', `${Math.round(progress * 18)}px`);
    });
  };
  const queueParallax = () => {
    if (parallaxQueued) return;
    parallaxQueued = true;
    requestAnimationFrame(updateParallax);
  };
  window.addEventListener('scroll', queueParallax, { passive: true });
  window.addEventListener('resize', queueParallax, { passive: true });
  reduced.addEventListener('change', () => {
    if (reduced.matches) document.querySelectorAll('[data-sl-motion-pending]').forEach((node) => { node.removeAttribute('data-sl-motion-pending'); node.classList.add('is-sl-visible'); });
    queueParallax();
  });
  const setup = (root = document) => { setupHero(root); setupReveals(root); setupAmounts(root); setupComparison(root); setupGallery(root); queueParallax(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();